import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { ChatConversation, ChatMessage } from '../types';

/**
 * The stripped mobile view an SMS magic link opens. One conversation, no
 * login, no dashboard: an agent on their phone reads the transcript, replies
 * (which takes the chat over), and closes it. The link is bound to the first
 * device that opens it and dies after 8 hours (Flow-Spec §6 #5); the server
 * enforces both — this page just shows the right message when told no.
 */

type Payload = {
  conversation: Pick<ChatConversation,
    'id' | 'status' | 'inquiry_type' | 'visitor_name' | 'visitor_phone' | 'visitor_email'
    | 'visitor_zip' | 'care_recipient' | 'ltci_intent' | 'assigned_user_id'>;
  messages: ChatMessage[];
  me: { user_id: number; display_name: string | null };
  agents: Record<string, { user_id: number; display_name: string | null; photo_url: string | null }>;
  franchise: string;
  expires_at: string;
};

const INQUIRY: Record<string, string> = {
  home_care_lead: 'Home care', caregiver_lead: 'Caregiver (job)', ltci_policy_review: 'LTCi policy review', unknown: 'Website chat',
};

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, { credentials: 'include', ...init });
  const data = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) throw Object.assign(new Error(data.error ?? `${res.status}`), { status: res.status });
  return data;
}

export default function ChatMagicPage() {
  const { token = '' } = useParams();
  const [data, setData] = useState<Payload | null>(null);
  const [dead, setDead] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const logRef = useRef<HTMLDivElement>(null);

  async function load() {
    try {
      const d = await api<Payload>(`/api/chat-magic/${encodeURIComponent(token)}`);
      setData(d);
      setDead(null);
    } catch (e) {
      const status = (e as { status?: number }).status;
      if (status === 410 || status === 403 || status === 404) setDead((e as Error).message);
    }
  }

  useEffect(() => {
    let alive = true;
    void load();
    const t = setInterval(() => { if (alive && !dead && data?.conversation.status !== 'closed') void load(); }, 3000);
    return () => { alive = false; clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, dead, data?.conversation.status]);

  useEffect(() => {
    const n = logRef.current;
    if (n) n.scrollTop = n.scrollHeight;
  }, [data?.messages.length]);

  async function send() {
    const text = draft.trim();
    if (!text) return;
    setBusy(true); setErr(null);
    try {
      await api(`/api/chat-magic/${encodeURIComponent(token)}/messages`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ body: text }),
      });
      setDraft('');
      await load();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function close() {
    if (!window.confirm('Close this conversation?')) return;
    setBusy(true);
    try {
      await api(`/api/chat-magic/${encodeURIComponent(token)}/close`, { method: 'POST' });
      await load();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (dead) {
    return (
      <div style={shell}>
        <header style={head}><strong>Amada chat</strong></header>
        <main style={{ padding: 24, textAlign: 'center' }}>
          <p style={{ fontSize: 16, margin: '24px 0 8px' }}>{dead}</p>
          <p style={muted}>Open the chat from the dashboard instead.</p>
        </main>
      </div>
    );
  }
  if (!data) {
    return <div style={shell}><header style={head}><strong>Amada chat</strong></header><main style={{ padding: 24 }}><p style={muted}>Opening…</p></main></div>;
  }

  const c = data.conversation;
  const closed = c.status === 'closed';
  const who = (uid: number | null) => (uid != null ? data.agents[String(uid)]?.display_name || 'Teammate' : 'Team');

  return (
    <div style={shell}>
      <header style={head}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700 }}>{c.visitor_name || 'Website visitor'}</div>
          <div style={{ fontSize: 12, opacity: 0.85 }}>
            {INQUIRY[c.inquiry_type] ?? c.inquiry_type} · {data.franchise}
            {c.care_recipient ? ` · for ${c.care_recipient === 'self' ? 'themselves' : 'a loved one'}` : ''}
            {c.ltci_intent ? ` · ${c.ltci_intent === 'claim' ? 'file a claim' : 'review policy'}` : ''}
          </div>
          <div style={{ fontSize: 12, opacity: 0.85 }}>
            {[c.visitor_phone, c.visitor_email, c.visitor_zip ? `ZIP ${c.visitor_zip}` : null].filter(Boolean).join(' · ') || 'No contact details yet'}
          </div>
        </div>
        {!closed && <button onClick={close} disabled={busy} style={ghostBtn}>Close chat</button>}
      </header>

      <div ref={logRef} style={{ flex: 1, overflowY: 'auto', padding: 14, display: 'flex', flexDirection: 'column', gap: 8, background: '#f8fafc' }}>
        {data.messages.length === 0 && <p style={muted}>Nothing said yet.</p>}
        {data.messages.map((m) => {
          const isAgent = m.role === 'agent';
          const isVisitor = m.role === 'visitor';
          return (
            <div key={m.seq} style={{ display: 'flex', justifyContent: isAgent ? 'flex-end' : 'flex-start' }}>
              <div style={{
                maxWidth: '82%', padding: '9px 12px', borderRadius: 14, fontSize: 15, lineHeight: 1.4,
                background: isAgent ? '#1f2a48' : isVisitor ? '#fff' : '#eef2f7', color: isAgent ? '#fff' : '#1c1c1c',
                border: isVisitor ? '1px solid #e2e8f0' : 'none',
              }}>
                <div style={{ fontSize: 11, opacity: 0.75, marginBottom: 2 }}>
                  {isAgent ? (m.author_user_id === data.me.user_id ? 'You' : who(m.author_user_id)) : isVisitor ? (c.visitor_name || 'Visitor') : 'Assistant'}
                </div>
                {m.body}
              </div>
            </div>
          );
        })}
      </div>

      {closed ? (
        <footer style={{ padding: 14, borderTop: '1px solid #e2e8f0', textAlign: 'center', ...muted }}>This conversation is closed.</footer>
      ) : (
        <footer style={{ padding: 10, borderTop: '1px solid #e2e8f0', background: '#fff' }}>
          {err && <p style={{ color: '#b91c1c', fontSize: 13, margin: '0 0 6px' }}>{err}</p>}
          <form onSubmit={(e) => { e.preventDefault(); void send(); }} style={{ display: 'flex', gap: 8 }}>
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={c.assigned_user_id === data.me.user_id ? 'Reply…' : 'Reply to join the chat…'}
              style={{ flex: 1, padding: '11px 14px', border: '1px solid #cbd5e1', borderRadius: 999, fontSize: 16, fontFamily: 'inherit' }}
              disabled={busy}
              autoComplete="off"
            />
            <button type="submit" disabled={busy || !draft.trim()} style={sendBtn}>Send</button>
          </form>
          <p style={{ ...muted, margin: '8px 0 0', textAlign: 'center', fontSize: 11 }}>
            This link opens only this conversation and expires {new Date(data.expires_at).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}.
          </p>
        </footer>
      )}
    </div>
  );
}

const shell: React.CSSProperties = {
  position: 'fixed', inset: 0, display: 'flex', flexDirection: 'column', background: '#fff',
  fontFamily: "'Segoe UI', system-ui, -apple-system, Arial, sans-serif", color: '#1c1c1c',
};
const head: React.CSSProperties = { background: '#1f2a48', color: '#fff', padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 10 };
const muted: React.CSSProperties = { fontSize: 13, color: '#64748b' };
const ghostBtn: React.CSSProperties = { background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,.5)', borderRadius: 8, padding: '6px 10px', fontSize: 13, fontFamily: 'inherit' };
const sendBtn: React.CSSProperties = { border: 'none', background: '#e87722', color: '#fff', borderRadius: 999, padding: '0 18px', fontWeight: 700, fontFamily: 'inherit', fontSize: 15 };
